import React, { useEffect, useMemo, useState } from 'react';
import { Ban, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Textarea } from '../../ui/textarea';
import type { ScopeKey } from '../../../lib/rbac/permissions.generated';
import {
  adminUsersApi,
  standortUsersApi,
  type Katalog,
  type Override,
  type OverridePayload,
} from '../../../lib/rbac/userAdmin';

interface Props {
  mode: 'admin' | 'standort';
  accountId: string;
  katalog: Katalog;
  /** Standort-Modus: Standorte, an denen der Aufrufer individuelle Rechte vergeben darf. */
  standortIds?: number[];
}

const MAX_TREFFER = 60;
const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/**
 * Individuelle Rechte (RBAC-SPEC D.6). Ob ein Recht weitergegeben werden darf, prüft
 * der Server; die Liste blendet nur aus, was der Standort-Weg nie zulässt.
 */
const OverridesPanel: React.FC<Props> = ({ mode, accountId, katalog, standortIds = [] }) => {
  const client = mode === 'admin' ? adminUsersApi : standortUsersApi;
  const [rows, setRows] = useState<Override[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [permissionKey, setPermissionKey] = useState('');
  const [effect, setEffect] = useState<'grant' | 'revoke'>('grant');
  const [scope, setScope] = useState<ScopeKey>(mode === 'admin' ? 'team' : 'location');
  const [standortId, setStandortId] = useState<string>(standortIds[0] ? String(standortIds[0]) : '');
  const [maxValue, setMaxValue] = useState('');
  const [perMonth, setPerMonth] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const labelOf = useMemo(() => new Map(katalog.permissions.map((p) => [p.key, p])), [katalog]);
  const standortName = useMemo(() => new Map(katalog.standorte.map((s) => [s.id, s.name])), [katalog]);
  const scopeName = useMemo(() => new Map(katalog.scopes.map((s) => [s.key, s.label])), [katalog]);

  const treffer = useMemo(() => {
    const q = search.trim().toLowerCase();
    return katalog.permissions
      .filter((p) => mode === 'admin' || p.delegierbar_am_standort)
      .filter(
        (p) =>
          !q ||
          p.label.toLowerCase().includes(q) ||
          p.key.toLowerCase().includes(q) ||
          p.section_title.toLowerCase().includes(q),
      )
      .slice(0, MAX_TREFFER);
  }, [katalog, mode, search]);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await client.overrides(accountId));
    } catch (e) {
      toast.error(errorText(e, 'Individuelle Rechte konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, mode]);

  const create = async () => {
    if (!permissionKey) return toast.error('Bitte ein Recht auswählen');
    if (!reason.trim()) return toast.error('Bitte eine Begründung angeben');
    const payload: OverridePayload = {
      permission_key: permissionKey,
      effect: mode === 'admin' ? effect : 'grant',
      reason: reason.trim(),
      valid_until: validUntil ? new Date(`${validUntil}T23:59:59`).toISOString() : null,
    };
    if (payload.effect === 'grant') {
      payload.scope = mode === 'admin' ? scope : 'location';
      if (payload.scope === 'location') {
        if (!standortId) return toast.error('Bitte einen Standort auswählen');
        payload.standort_id = Number(standortId);
      }
      if (mode === 'admin' && maxValue.trim()) {
        const value = Number(maxValue.replace(',', '.'));
        if (!Number.isFinite(value) || value <= 0) return toast.error('Wertgrenze muss größer 0 sein');
        payload.limits = perMonth ? { max_value_eur: value, per: 'month' } : { max_value_eur: value };
      }
    }
    setSaving(true);
    try {
      await client.createOverride(accountId, payload);
      toast.success('Individuelles Recht gespeichert');
      setPermissionKey('');
      setReason('');
      setMaxValue('');
      setValidUntil('');
      await load();
    } catch (e) {
      toast.error(errorText(e, 'Individuelles Recht konnte nicht gespeichert werden'));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (row: Override) => {
    const why = window.prompt('Begründung für das Beenden dieses Rechts:');
    if (!why || !why.trim()) return;
    try {
      await client.deactivateOverride(accountId, row.id, why.trim());
      toast.success('Recht beendet');
      await load();
    } catch (e) {
      toast.error(errorText(e, 'Recht konnte nicht beendet werden'));
    }
  };

  const selected = permissionKey ? labelOf.get(permissionKey as Override['permission_key']) : undefined;

  return (
    <div className="space-y-5">
      <div className="rounded-md border">
        <Table density="compact">
          <TableHeader>
            <TableRow>
              <TableHead density="compact">Recht</TableHead>
              <TableHead density="compact">Wirkung</TableHead>
              <TableHead density="compact">Begründung</TableHead>
              <TableHead density="compact">Bis</TableHead>
              <TableHead density="compact" className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell density="compact" colSpan={5}>Lade…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell density="compact" colSpan={5} className="text-muted-foreground">
                  Keine individuellen Rechte.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const def = labelOf.get(row.permission_key);
              return (
                <TableRow key={row.id} className={row.active ? '' : 'opacity-60'}>
                  <TableCell density="compact">
                    <div className="font-medium">{def?.label ?? row.permission_key}</div>
                    <div className="text-xs text-muted-foreground">{def?.section_title}</div>
                  </TableCell>
                  <TableCell density="compact">
                    <div className="flex flex-wrap gap-1">
                      <Badge size="sm" variant={row.effect === 'grant' ? 'success' : 'destructive'}>
                        {row.effect === 'grant' ? 'Gewährt' : 'Entzogen'}
                      </Badge>
                      {row.scope && <Badge size="sm" variant="outline">{scopeName.get(row.scope) ?? row.scope}</Badge>}
                      {row.standort_id && (
                        <Badge size="sm" variant="outline">{standortName.get(row.standort_id) ?? `Standort ${row.standort_id}`}</Badge>
                      )}
                      {row.limits?.max_value_eur != null && (
                        <Badge size="sm" variant="warning">
                          max. {row.limits.max_value_eur.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          {row.limits.per === 'month' ? ' / Monat' : ''}
                        </Badge>
                      )}
                      {!row.active && <Badge size="sm" variant="secondary">beendet</Badge>}
                    </div>
                  </TableCell>
                  <TableCell density="compact" className="max-w-xs text-sm">{row.reason}</TableCell>
                  <TableCell density="compact" className="text-sm">
                    {row.valid_until ? new Date(row.valid_until).toLocaleDateString('de-DE') : '—'}
                  </TableCell>
                  <TableCell density="compact" className="text-right">
                    {row.active && (
                      <Button variant="outline" size="sm" onClick={() => deactivate(row)}>
                        <Ban className="mr-2 h-4 w-4" /> Beenden
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-3 rounded-md border p-3">
        <div className="text-sm font-medium">Individuelles Recht hinzufügen</div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Recht suchen</Label>
            <Input uiSize="sm" value={search} placeholder="z. B. Gutschein, Raumplan …"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} />
          </div>
          <div>
            <Label>Recht</Label>
            <Select value={permissionKey} onValueChange={setPermissionKey}>
              <SelectTrigger size="sm"><SelectValue placeholder="Recht auswählen" /></SelectTrigger>
              <SelectContent>
                {treffer.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.section_title}: {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {selected && <p className="text-xs text-muted-foreground">{selected.description}</p>}

        <div className="grid gap-3 md:grid-cols-3">
          {mode === 'admin' && (
            <div>
              <Label>Wirkung</Label>
              <Select value={effect} onValueChange={(v: string) => setEffect(v as 'grant' | 'revoke')}>
                <SelectTrigger size="sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="grant">Gewähren</SelectItem>
                  <SelectItem value="revoke">Entziehen (Sperre, gilt überall)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {mode === 'admin' && effect === 'grant' && (
            <div>
              <Label>Scope</Label>
              <Select value={scope} onValueChange={(v: string) => setScope(v as ScopeKey)}>
                <SelectTrigger size="sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {katalog.scopes.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {effect === 'grant' && (mode === 'standort' || scope === 'location') && (
            <div>
              <Label>Standort</Label>
              <Select value={standortId} onValueChange={setStandortId}>
                <SelectTrigger size="sm"><SelectValue placeholder="Standort" /></SelectTrigger>
                <SelectContent>
                  {(mode === 'admin' ? katalog.standorte.map((s) => s.id) : standortIds).map((id) => (
                    <SelectItem key={id} value={String(id)}>{standortName.get(id) ?? id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {mode === 'admin' && effect === 'grant' && (
            <div>
              <Label>Wertgrenze in € (optional)</Label>
              <div className="flex items-center gap-2">
                <Input uiSize="sm" inputMode="decimal" value={maxValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxValue(e.target.value)} />
                <label className="flex items-center gap-1 whitespace-nowrap text-xs">
                  <input type="checkbox" checked={perMonth} onChange={(e) => setPerMonth(e.target.checked)} />
                  je Monat
                </label>
              </div>
            </div>
          )}
          <div>
            <Label>Gültig bis (optional)</Label>
            <Input type="date" uiSize="sm" value={validUntil}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidUntil(e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Begründung</Label>
          <Textarea value={reason} rows={2}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)} />
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={create} disabled={saving}>
            <PlusCircle className="mr-2 h-4 w-4" /> Hinzufügen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OverridesPanel;
